/**
 * Shared types for chat agent events, sent via Server-Sent Events (SSE)
 */

// --- Error codes ---

/**
 * CONTEXT_EXHAUSTED: Conversation has reached context window limit, cannot continue
 * MAX_TURNS_REACHED: Agent reached max turns limit, user can prompt to continue
 * BUDGET_EXHAUSTED: Budget limit for this conversation has been reached
 * INTERNAL_ERROR: An unexpected server-side error occurred
 */
export type ChatAgentErrorCode =
  | 'CONTEXT_EXHAUSTED'
  | 'MAX_TURNS_REACHED'
  | 'BUDGET_EXHAUSTED'
  | 'INTERNAL_ERROR';

// --- Event data types ---

export interface ChatAgentTextEventData {
  text: string;
}

export interface ChatAgentToolStartEventData {
  toolName: string;
  toolUseId: string;
}

export interface ChatAgentToolProgressEventData {
  toolName: string;
  toolUseId: string;
  elapsedSeconds: number;
}

export interface ChatAgentToolUse {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ChatAgentMessageEventData {
  content: string;
  toolUses?: ChatAgentToolUse[];
}

export interface ChatAgentErrorEventData {
  message: string;
  code?: ChatAgentErrorCode;
}

export interface ChatAgentResultEventData {
  isError: boolean;
  totalCost: number;
}

export interface ChatAgentConversationEventData {
  conversationId: string;
}

export interface ChatAgentDoneEventData {
  finished: boolean;
}

/**
 * Status codes for system status events.
 * compacting: Agent is compacting conversation context to free up space
 * retrying: Agent is retrying after a retriable error
 */
export type ChatAgentStatusCode = 'compacting' | 'retrying';

export interface ChatAgentStatusEventData {
  status: ChatAgentStatusCode;
}

// --- SSE Event union type ---

export const CHAT_AGENT_EVENT_TYPES = [
  'text',
  'tool_start',
  'tool_progress',
  'message',
  'error',
  'result',
  'conversation',
  'done',
  'status',
] as const;

export type ChatAgentEventType = (typeof CHAT_AGENT_EVENT_TYPES)[number];

export type ChatAgentEvent =
  | { type: 'text'; data: ChatAgentTextEventData }
  | { type: 'tool_start'; data: ChatAgentToolStartEventData }
  | { type: 'tool_progress'; data: ChatAgentToolProgressEventData }
  | { type: 'message'; data: ChatAgentMessageEventData }
  | { type: 'error'; data: ChatAgentErrorEventData }
  | { type: 'result'; data: ChatAgentResultEventData }
  | { type: 'conversation'; data: ChatAgentConversationEventData }
  | { type: 'done'; data: ChatAgentDoneEventData }
  | { type: 'status'; data: ChatAgentStatusEventData };

/**
 * Helper to get the data type for a specific event type.
 * Useful for type-safe event handlers.
 */
export type ChatAgentEventDataFor<T extends ChatAgentEventType> = Extract<
  ChatAgentEvent,
  { type: T }
>['data'];

/**
 * Exhaustive switch helper - use in default case to ensure all events are handled.
 * @example
 * switch (event.type) {
 *   case 'text': ...
 *   case 'error': ...
 *   default: assertNeverChatAgentEvent(event);
 * }
 */
export function assertNeverChatAgentEvent(event: never): never {
  throw new Error(`Unhandled chat agent event: ${JSON.stringify(event)}`);
}
